/**
 * EQUIPE — quem usa o Pesagem nesta empresa. Gestor convida, muda papel e
 * desliga; Consulta só vê. O servidor e o banco conferem de novo.
 */
import { useEffect, useState } from 'react'
import { api, atualizarVinculo, Recusa, SemSinal, type Papel } from '../dados/sessao'
import { lerVinculo, VINCULOS, type Vinculo } from '../dominio/ferramentas'
import { avisar, Campo, Dica, Escolha, Janela, Segmento, Tarja } from '../ui'

interface PessoaDaEquipe { id: string; nome: string; email: string; papel: Papel; sigla: string; ativa: boolean; ja_entrou: boolean; vinculo?: Vinculo | null }

/**
 * 3C · Vínculo do comprador (decisão do gestor, 06/10/2026): diz o PADRÃO de
 * «por conta de quem» da despesa de viagem — contratado (salário) → da empresa;
 * comissionado → dele. O padrão se troca em cada despesa. '' = não definido
 * (o app pergunta na hora). Só o gestor grava; o banco confere (migração 011).
 */
type EscolhaDeVinculo = Vinculo | ''
const NOME_DO_VINCULO: Record<Vinculo, string> = { contratado: 'contratado', comissionado: 'comissionado' }

function EscolherVinculo({ valor, mudar }: { valor: EscolhaDeVinculo; mudar: (v: EscolhaDeVinculo) => void }) {
  return (
    <>
      <div className="campo"><span>Vínculo do comprador</span></div>
      <Segmento<EscolhaDeVinculo> valor={valor} mudar={mudar}
        opcoes={[...VINCULOS.map(([v, t]) => [v, t] as [EscolhaDeVinculo, string]), ['', 'Não definido']]} />
      <div className="mini-dica">{valor
        ? VINCULOS.find(([v]) => v === valor)![2]
        : 'sem vínculo, o app pergunta de quem é a despesa a cada lançamento'}. Dá para trocar em cada despesa.</div>
    </>
  )
}

const NOME_DO_PAPEL: Record<Papel, string> = { gestor: 'Gestor', comprador: 'Comprador', consulta: 'Consulta' }
const EXPLICA_PAPEL: Record<Papel, string> = {
  gestor: 'vê tudo, cadastra pessoas, lança comissão e pagamentos',
  comprador: 'pesa e negocia; vê só as próprias fichas e produtores',
  consulta: 'vê tudo e imprime, não altera nada (escritório, secretária)',
}

function mensagemDe(e: unknown): string {
  if (e instanceof SemSinal) return 'Sem internet agora. A equipe só é mexida com sinal.'
  if (e instanceof Recusa) return e.message
  return 'Não deu para concluir. Tente de novo.'
}

/** Copia o link do convite; se o navegador não deixar, mostra para copiar à mão. */
export async function copiarLink(link: string): Promise<void> {
  try { await navigator.clipboard.writeText(link); avisar('Link copiado — cole no WhatsApp') }
  catch { window.prompt('Copie o link do convite:', link) }
}

export function Equipe({ souGestor, euId }: { souGestor: boolean; euId: string }) {
  const [pessoas, setPessoas] = useState<PessoaDaEquipe[] | null>(null)
  // antes da migração 011 o banco não tem onde guardar o vínculo: a escolha nem aparece
  const [comVinculo, setComVinculo] = useState(false)
  const [erro, setErro] = useState('')
  const [novo, setNovo] = useState<{ nome: string; email: string; papel: Papel; sigla: string; vinculo: EscolhaDeVinculo } | null>(null)
  const [editando, setEditando] = useState<(PessoaDaEquipe & { escolha: EscolhaDeVinculo }) | null>(null)
  const [indo, setIndo] = useState(false)
  const [ultimoLink, setUltimoLink] = useState<{ nome: string; link: string; email: boolean } | null>(null)

  const carregar = () => api<{ pessoas: PessoaDaEquipe[]; sem_vinculo?: boolean }>('pessoas')
    .then((r) => { setPessoas(r.pessoas); setComVinculo(!r.sem_vinculo) })
    .catch((e) => setErro(mensagemDe(e)))
  useEffect(() => { carregar() }, [])

  async function convidar() {
    if (!novo) return
    setIndo(true)
    try {
      const { vinculo, ...dados } = novo
      const corpo = comVinculo && novo.papel === 'comprador' ? { ...dados, vinculo: vinculo || null } : dados
      const r = await api<{ link: string; email_enviado: boolean; pessoa_id?: string }>('pessoas', corpo)
      if (r.pessoa_id && 'vinculo' in corpo) atualizarVinculo(r.pessoa_id, lerVinculo(corpo.vinculo))
      setUltimoLink({ nome: novo.nome, link: r.link, email: r.email_enviado })
      setNovo(null); carregar()
    } catch (e) { window.alert(mensagemDe(e)) } finally { setIndo(false) }
  }
  async function reconvidar(p: PessoaDaEquipe) {
    try {
      const r = await api<{ link: string; email_enviado: boolean }>('pessoas/reconvidar', { id: p.id })
      setUltimoLink({ nome: p.nome, link: r.link, email: r.email_enviado })
    } catch (e) { window.alert(mensagemDe(e)) }
  }
  async function salvarEdicao() {
    if (!editando) return
    setIndo(true)
    try {
      // o vínculo só vai quando mudou: assim a Equipe continua funcionando antes da 011
      const vinculo = lerVinculo(editando.escolha)
      const mudouVinculo = comVinculo && vinculo !== (editando.vinculo ?? null)
      await api('pessoas/alterar', { id: editando.id, nome: editando.nome, papel: editando.papel, sigla: editando.sigla, ativa: editando.ativa,
        ...(mudouVinculo ? { vinculo } : {}) })
      // o padrão da despesa já vale neste aparelho, sem esperar reabrir o app
      if (mudouVinculo) atualizarVinculo(editando.id, vinculo)
      setEditando(null); carregar(); avisar('Equipe atualizada')
    } catch (e) { window.alert(mensagemDe(e)) } finally { setIndo(false) }
  }

  return (
    <div>
      <h2 className="secao">Equipe</h2>
      {erro && <Tarja tipo="erro">{erro}</Tarja>}
      {ultimoLink && (
        <Tarja tipo="ok">
          Convite de {ultimoLink.nome} pronto. {ultimoLink.email ? 'O e-mail foi enviado.' : 'O e-mail NÃO saiu (domínio ainda em verificação) — mande o link pelo WhatsApp.'}{' '}
          <button className="mini" onClick={() => copiarLink(ultimoLink.link)}>Copiar link</button>
        </Tarja>
      )}
      <div className="cartao">
        {!pessoas ? <div className="vazio">Carregando…</div> : pessoas.map((p) => (
          <div key={p.id} className={`item${souGestor ? ' clicavel' : ''}`} onClick={() => souGestor && setEditando({ ...p, escolha: p.vinculo ?? '' })}>
            <div className="item-topo">
              <span className="item-titulo">{p.nome}{p.id === euId ? ' (você)' : ''}</span>
              <span className="mini-dica">{NOME_DO_PAPEL[p.papel]}{p.papel === 'comprador' && p.vinculo ? ` ${NOME_DO_VINCULO[p.vinculo]}` : ''} · {p.sigla}</span>
            </div>
            <div className="item-baixo">
              <span>{p.email}</span>
              <span>
                {!p.ativa && <span className="etiqueta cancelada">desligado</span>}
                {p.ativa && !p.ja_entrou && <span className="etiqueta aberta">convite pendente</span>}
              </span>
            </div>
          </div>
        ))}
        {souGestor && <button className="botao fantasma" onClick={() => setNovo({ nome: '', email: '', papel: 'comprador', sigla: '', vinculo: '' })}>+ Convidar pessoa</button>}
      </div>
      <Dica>
        <b>Gestor:</b> {EXPLICA_PAPEL.gestor}.<br /><b>Comprador:</b> {EXPLICA_PAPEL.comprador}.<br /><b>Consulta:</b> {EXPLICA_PAPEL.consulta}.
      </Dica>

      {novo && (
        <Janela titulo="Convidar pessoa" fechar={() => setNovo(null)}>
          <Campo rotulo="Nome" valor={novo.nome} mudar={(v) => setNovo({ ...novo, nome: v })} />
          <Campo rotulo="E-mail (é o login da pessoa)" tipo="email" valor={novo.email} mudar={(v) => setNovo({ ...novo, email: v })} />
          <Escolha<Papel> rotulo="Papel" valor={novo.papel} mudar={(v) => setNovo({ ...novo, papel: v })}
            opcoes={[['comprador', 'Comprador'], ['consulta', 'Consulta'], ['gestor', 'Gestor']]} />
          <div className="mini-dica">{EXPLICA_PAPEL[novo.papel]}</div>
          {comVinculo && novo.papel === 'comprador' && <EscolherVinculo valor={novo.vinculo} mudar={(v) => setNovo({ ...novo, vinculo: v })} />}
          <Campo rotulo="Sigla (iniciais, vão no número das fichas)" valor={novo.sigla} mudar={(v) => setNovo({ ...novo, sigla: v.toUpperCase() })} max={4} maiusculas dica="ex.: AJ" />
          <Dica>A pessoa recebe um e-mail com o link para criar a própria senha (vale 7 dias). O link também aparece aqui para você mandar pelo WhatsApp.</Dica>
          <button className="botao" disabled={indo} onClick={convidar}>{indo ? 'Enviando…' : 'Mandar convite'}</button>
          <button className="botao fantasma" onClick={() => setNovo(null)}>Fechar</button>
        </Janela>
      )}

      {editando && (
        <Janela titulo={editando.nome} fechar={() => setEditando(null)}>
          <Campo rotulo="Nome" valor={editando.nome} mudar={(v) => setEditando({ ...editando, nome: v })} />
          <div className="mini-dica">Login: {editando.email}</div>
          <Escolha<Papel> rotulo="Papel" valor={editando.papel} mudar={(v) => setEditando({ ...editando, papel: v })} desabilitado={editando.id === euId}
            opcoes={[['comprador', 'Comprador'], ['consulta', 'Consulta'], ['gestor', 'Gestor']]} />
          {comVinculo && editando.papel === 'comprador' && <EscolherVinculo valor={editando.escolha} mudar={(v) => setEditando({ ...editando, escolha: v })} />}
          <Campo rotulo="Sigla" valor={editando.sigla} mudar={(v) => setEditando({ ...editando, sigla: v.toUpperCase() })} max={4} maiusculas />
          <button className="botao" disabled={indo} onClick={salvarEdicao}>Salvar</button>
          {editando.ativa && !editando.ja_entrou && <button className="botao fantasma" onClick={() => reconvidar(editando)}>Mandar o convite de novo</button>}
          {editando.id !== euId && (
            editando.ativa
              ? <button className="botao perigo" onClick={() => { if (window.confirm(`Desligar ${editando.nome}? Os aparelhos dela saem na hora; as fichas continuam na empresa.`)) setEditando({ ...editando, ativa: false }) }}>Desligar acesso</button>
              : <button className="botao fantasma" onClick={() => setEditando({ ...editando, ativa: true })}>Religar acesso</button>
          )}
          {!editando.ativa && <Dica>Desligado — toque em Salvar para confirmar.</Dica>}
          <button className="botao fantasma" onClick={() => setEditando(null)}>Fechar</button>
        </Janela>
      )}
    </div>
  )
}
